// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { advisoryApi } from '@shared/data-access/api/brokerage/advisory-api.v2';
import { useDebounce } from '@shared/ui-kit/hooks';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePlaidConnection } from '../../hooks/use-plaid-connection';
import { useWealthReaderConnection } from '../../hooks/use-wealth-reader-connection';

type CountryOption = {
  code: string;
  label: string;
};

type CountrySelectorOption = {
  value: string;
  label: string;
};

export type BanksConnectProps = {
  /** `drawer` trims page chrome for embedding in the bank-upload drawer. */
  variant?: 'page' | 'drawer';
  /** When set (drawer embed), shows Previous aligned with Continue. */
  onBack?: () => void;
};

export const useBanksConnect = ({
  variant = 'page',
  onBack,
}: BanksConnectProps = {}) => {
  const { t, i18n } = useTranslation();

  const countryCodes = useMemo(
    () =>
      t('aggregator.countries', {
        ns: 'aggregator',
        returnObjects: true,
      }) as unknown as string[],
    [t],
  );

  const countryOptions = useMemo<CountryOption[]>(() => {
    const displayNames = new Intl.DisplayNames([i18n.resolvedLanguage ?? 'en'], {
      type: 'region',
    });

    return countryCodes
      .map(code => ({
        code,
        label: displayNames.of(code) ?? code,
      }))
      .sort((countryA, countryB) =>
        countryA.label.localeCompare(countryB.label, i18n.resolvedLanguage, {
          sensitivity: 'base',
        }),
      );
  }, [countryCodes, i18n.resolvedLanguage]);

  const [country, setCountry] = useState('');

  const [search, setSearch] = useState('');

  const [selectedInstitutionKey, setSelectedInstitutionKey] = useState<string>();

  const debouncedSearch = useDebounce(search, 300);

  const { handleLinkAccount, isLoading: isPlaidLoading } = usePlaidConnection();

  const { handleWealthReaderConnection } = useWealthReaderConnection();

  const searchInstitutionsQuery = advisoryApi.useSearchAdvisoryInstitutionsQuery({
    country: country || undefined,
    q: debouncedSearch.trim() || undefined,
  });

  const selectorCountryOptions = useMemo<CountrySelectorOption[]>(
    () =>
      [
        { value: '', label: t('aggregator.all-countries', { ns: 'aggregator' }) },
      ].concat(
        countryOptions.map(option => ({
          value: option.code,
          label: option.label,
        })),
      ),
    [countryOptions, t],
  );

  const institutions = useMemo(
    () =>
      (searchInstitutionsQuery.data ?? []).map(institution => {
        const listKey = `${institution.provider}-${institution.country}-${institution.id}`;

        return {
          ...institution,
          listKey,
          isSelected: selectedInstitutionKey === listKey,
        };
      }),
    [searchInstitutionsQuery.data, selectedInstitutionKey],
  );

  const countryByCode = useMemo(
    () =>
      new Map([
        ['ALL', t('aggregator.available-all-countries', { ns: 'aggregator' })],
        ...countryOptions.map(item => [item.code, item.label] as const),
      ]),
    [countryOptions, t],
  );

  const selectedInstitution = useMemo(
    () => institutions.find(item => item.listKey === selectedInstitutionKey),
    [institutions, selectedInstitutionKey],
  );

  const onContinue = useCallback(async () => {
    if (!selectedInstitution) {
      return;
    }

    if (selectedInstitution.provider === 'plaid') {
      await handleLinkAccount(selectedInstitution.id);

      return;
    }

    if (selectedInstitution.provider === 'wealth_reader') {
      await handleWealthReaderConnection({
        entitiesToDisplay: [selectedInstitution.id],
        defaultLogin: selectedInstitution.id,
        defaultLoginEntityCountry: selectedInstitution.country,
        oneShot: selectedInstitution.is_oneshot,
      });
    }
  }, [handleLinkAccount, handleWealthReaderConnection, selectedInstitution]);

  const onCountryChange = useCallback((value: string) => {
    setCountry(value);
    setSelectedInstitutionKey(undefined);
  }, []);

  const onSearchChange = useCallback((value: string) => {
    setSearch(value);
    setSelectedInstitutionKey(undefined);
  }, []);

  const onSelectInstitution = useCallback((institutionKey: string) => {
    setSelectedInstitutionKey(institutionKey);
  }, []);

  const isLoading = useMemo(
    () => searchInstitutionsQuery.isLoading || searchInstitutionsQuery.isFetching,
    [searchInstitutionsQuery.isFetching, searchInstitutionsQuery.isLoading],
  );

  const hasInstitutions = useMemo(
    () => institutions.length > 0,
    [institutions.length],
  );

  const canContinue = useMemo(
    () => Boolean(selectedInstitutionKey),
    [selectedInstitutionKey],
  );

  return {
    t,
    variant,
    isDrawer: variant === 'drawer',
    countryOptions: selectorCountryOptions,
    country,
    search,
    institutions,
    countryByCode,
    selectedInstitutionKey,
    isLoading,
    isError: searchInstitutionsQuery.isError,
    isConnecting: isPlaidLoading,
    onRetry: searchInstitutionsQuery.refetch,
    onCountryChange,
    onSearchChange,
    onSelectInstitution,
    onContinue,
    onBack,
    hasInstitutions,
    canContinue,
  };
};
